/**
 * Step 4–5 · Mic prime + recording (ux-flows §2 step 5, design-spec/07, plates 03–04).
 *
 * The referee's ~3-minute take, on their own phone. The hard rules, in order:
 *  1. PRIME FIRST — a plain sentence, then a mic check that fires `getUserMedia` from the user's
 *     own tap (iOS Safari needs HTTPS + a gesture). Nothing records until they tap record.
 *  2. Negotiate the MIME type via `MediaRecorder.isTypeSupported` and STORE EXACTLY WHAT THE
 *     BROWSER PRODUCED — never transcode (see `src/lib/recorder.ts`).
 *  3. ONE take: a single blob at stop() (no mid-record chunking), a 3-minute hard cap with a
 *     remaining-time countdown (aria-live), pause/resume and a full "Start over".
 *  4. On stop: init (exact byte length) → a single retryable constrained PUT → complete, then
 *     advance to processing. The completed answer is retained so a failed upload can be retried.
 *  5. THE TAKE DOES NOT LIVE HERE. It is held in the session store (`session/store.ts`), because a
 *     take held in this component's state dies with any unmount — and nothing has been uploaded at
 *     that point, so that is the whole three minutes gone. Everything on this screen therefore
 *     stays on this screen: a consent denial is answered in place rather than by a redirect.
 *
 * Motion is CSS only — framer-motion is reserved for the WP-F7 seal ceremony and kept out of this
 * first-paint path.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { apiClient } from '@/lib/api-client';
import { RefereeApiError, type ConsentAttestation } from '@/lib/api-types';
import { usePrefersReducedMotion } from '@/lib/motion';
import {
  createLevelMeter,
  createRecorderSession,
  isRecordingSupported,
  negotiateMimeType,
  prepareAudioPlayback,
  uploadRecording,
  type CompletedRecording,
  type LevelMeter,
  type RecorderHandle,
} from '@/lib/recorder';
import {
  createRealtimeTranscription,
  type RealtimeConnectionState,
  type RealtimeTranscriptionHandle,
} from '@/lib/realtime-transcription';
import { cn } from '@/lib/utils';
import { useAdvance } from '@/session/useAdvance';
import { useSession } from '@/session/useSession';
import { useSessionStore } from '@/session/store';

/**
 * The 422 `audio/init` raises when no `referee_attestation` consent is on record for the request.
 *
 * It is the ONLY consent code that can reach this screen: `AudioEndpoints` maps
 * `AudioInitOutcome.ConsentRequired` to exactly this string, and `consent_version_mismatch` is
 * raised solely by the consent endpoint (`RefereeFlowEndpoints`), which this screen only calls on
 * the retry below — where a stale version is not a thing the referee can be asked to fix.
 */
const CONSENT_REQUIRED = 'consent_required';
/** The 409 the consent endpoint raises when the request is not in a state that can consent. */
const CANNOT_CONSENT = 'cannot_consent';

function errorCodeOf(error: unknown): string | undefined {
  return error instanceof RefereeApiError ? error.code : undefined;
}

/**
 * How the review panel's send is going. Everything the referee is told at this step is derived from
 * exactly this, so each outcome gets its own name rather than being folded into "it failed":
 *  - `needs-consent` (422 `consent_required`) is one tap from succeeding;
 *  - `consent-refused` (409 `cannot_consent`) is not, and must not be dressed up as a retry;
 *  - `failed` is the ordinary transport failure the existing retry copy already covers.
 */
type SendState =
  | 'idle'
  | 'sending'
  | 'sending-consent'
  | 'failed'
  | 'needs-consent'
  | 'consent-refused';

type Phase =
  | 'intro' // explain + "Start mic check" (pre-gesture)
  | 'requesting' // getUserMedia in flight
  | 'ready' // mic granted, live check panel + "Start recording"
  | 'recording'
  | 'paused'
  | 'finishing'
  | 'review' // stopped, has a blob → use / re-record
  | 'denied' // mic permission refused
  | 'nomic' // no mic hardware / unsupported browser
  | 'error'; // capture failed

type LiveNotesState = RealtimeConnectionState | 'updating' | 'ready';

const DEFAULT_MAX_SECONDS = 180;
const NEAR_LIMIT_SECONDS = 30;
const HEARD_LEVEL = 0.08;

/** The pinned assurance (design-spec/07 §2) — the canonical string, never paraphrased. */
const ASSURANCE = 'Only you hear this until you approve it.';

function buildPrompts(firstName: string): string[] {
  return [
    `How do you know ${firstName}?`,
    'A strength, or a moment that stands out.',
    `Would you work with ${firstName} again?`,
  ];
}

function formatClock(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}:${rem.toString().padStart(2, '0')}`;
}

/* ============================================================================================== *
 * The capture hook — owns MediaRecorder, the meter, the clock and the phase machine.
 * ============================================================================================== */

interface VoiceRecorder {
  phase: Phase;
  level: number;
  elapsed: number;
  remaining: number;
  nearLimit: boolean;
  heard: boolean;
  announce: string;
  liveKeyPoints: string[];
  liveNotesState: LiveNotesState;
  recording: CompletedRecording | null;
  interrupted: boolean;
  errorMessage: string | null;
  requestMicCheck: () => void;
  startRecording: () => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  reRecord: () => void;
}

function useVoiceRecorder(maxSeconds: number, token: string): VoiceRecorder {
  // The completed take lives in the session store, NOT here — see rule 5 in the file header. The
  // phase machine still lives in component state, so it is seeded from the store: a remount that
  // finds a take already held must open on the review panel and re-offer it, never at `intro`
  // (which is the screen that asks someone to say it all again).
  const held = useSessionStore((s) => s.heldRecording);
  const holdRecording = useSessionStore((s) => s.holdRecording);
  const clearRecording = useSessionStore((s) => s.clearRecording);
  // A take stamped with another link's token is not ours to offer. `useSession` drops it on the
  // token change; this is the read-side half of the same rule, so a stale one is never rendered.
  const recording = held && held.token === token ? held : null;

  const [phase, setPhase] = useState<Phase>(() => {
    const existing = useSessionStore.getState().heldRecording;
    return existing && existing.token === token ? 'review' : 'intro';
  });
  const [level, setLevel] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [heard, setHeard] = useState(false);
  const [announce, setAnnounce] = useState('');
  const [liveKeyPoints, setLiveKeyPoints] = useState<string[]>([]);
  const [liveNotesState, setLiveNotesState] = useState<LiveNotesState>('connecting');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const setMicChecked = useSessionStore((s) => s.setMicChecked);

  const streamRef = useRef<MediaStream | null>(null);
  const sessionRef = useRef<RecorderHandle | null>(null);
  const meterRef = useRef<LevelMeter | null>(null);
  const realtimeRef = useRef<RealtimeTranscriptionHandle | null>(null);
  const realtimeShouldListenRef = useRef(false);
  const realtimeEligibleForReuseRef = useRef(false);
  const liveNotesActiveRef = useRef(false);
  const latestRealtimeTranscriptRef = useRef<CompletedRecording['realtimeTranscript']>(undefined);
  const keyPointTimerRef = useRef<number | null>(null);
  const keyPointInFlightRef = useRef(false);
  const lastKeyPointRequestAtRef = useRef(0);
  const lastRequestedTranscriptRef = useRef('');
  const scheduleKeyPointsRef = useRef<() => void>(() => undefined);
  const startedAtRef = useRef(0);
  const pausedAccumRef = useRef(0);
  const pauseStartedAtRef = useRef<number | null>(null);
  const heardRef = useRef(false);
  const phaseRef = useRef<Phase>('intro');
  const lastAnnouncedRef = useRef('');
  // Tears down the current take's interruption listeners (track mute/ended + document visibility).
  const detachInterruptionRef = useRef<(() => void) | null>(null);
  // Deferred "Recording started" announcement (H6) — set after the aria-live region mounts.
  const announceTimerRef = useRef<number | null>(null);
  // True once the mic input was suspended mid-take (iOS `mute`), so a still-suspended return is a cut.
  const interruptedByMuteRef = useRef(false);

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  const currentElapsedSeconds = useCallback(() => {
    if (startedAtRef.current === 0) return 0;
    const pausedNow = pauseStartedAtRef.current ? Date.now() - pauseStartedAtRef.current : 0;
    const activeMs = Date.now() - startedAtRef.current - pausedAccumRef.current - pausedNow;
    return Math.max(0, Math.floor(activeMs / 1000));
  }, []);

  const releaseStream = useCallback(async (prepareForPlayback = false) => {
    // Detach the interruption listeners first — the document-level visibilitychange handler must not
    // outlive the take (it would fire on every later tab switch).
    detachInterruptionRef.current?.();
    detachInterruptionRef.current = null;

    // Claim and clear both refs synchronously so a re-entrant cleanup cannot touch this capture.
    const meter = meterRef.current;
    meterRef.current = null;
    const stream = streamRef.current;
    streamRef.current = null;

    stream?.getTracks().forEach((track) => track.stop());
    try {
      // On iOS, exposing the player before this resolves can produce a moving seek bar with no sound.
      await meter?.close();
    } catch {
      // Cleanup is best-effort; never strand a completed take because a browser rejected close().
    }
    if (prepareForPlayback) await prepareAudioPlayback();
  }, []);

  const clearRealtime = useCallback(() => {
    realtimeShouldListenRef.current = false;
    liveNotesActiveRef.current = false;
    realtimeRef.current?.dispose();
    realtimeRef.current = null;
    realtimeEligibleForReuseRef.current = false;
    latestRealtimeTranscriptRef.current = undefined;
    if (keyPointTimerRef.current !== null) {
      clearTimeout(keyPointTimerRef.current);
      keyPointTimerRef.current = null;
    }
    keyPointInFlightRef.current = false;
    lastKeyPointRequestAtRef.current = 0;
    lastRequestedTranscriptRef.current = '';
  }, []);

  const teardown = useCallback(() => {
    if (announceTimerRef.current !== null) {
      clearTimeout(announceTimerRef.current);
      announceTimerRef.current = null;
    }
    sessionRef.current?.dispose();
    sessionRef.current = null;
    clearRealtime();
    void releaseStream();
  }, [clearRealtime, releaseStream]);

  // Best-effort teardown on unmount (releases the mic indicator).
  useEffect(() => () => teardown(), [teardown]);

  const extractLiveKeyPoints = useCallback(async () => {
    const transcript = latestRealtimeTranscriptRef.current;
    if (!liveNotesActiveRef.current || !transcript || keyPointInFlightRef.current) return;
    const wordCount = transcript.content.split(/\s+/).filter(Boolean).length;
    if (wordCount < 8 || transcript.content === lastRequestedTranscriptRef.current) return;

    keyPointInFlightRef.current = true;
    lastRequestedTranscriptRef.current = transcript.content;
    lastKeyPointRequestAtRef.current = Date.now();
    setLiveNotesState('updating');
    try {
      const points = await apiClient.extractLiveKeyPoints(token, transcript);
      setLiveKeyPoints(points);
      setLiveNotesState(points.length > 0 ? 'ready' : 'listening');
    } catch {
      // Live notes are optional. Do not disturb or stop a good recording because their preview
      // refresh failed; the verified audio and batch pipeline remain intact.
      setLiveNotesState('listening');
    } finally {
      keyPointInFlightRef.current = false;
      if (latestRealtimeTranscriptRef.current?.content !== lastRequestedTranscriptRef.current) {
        scheduleKeyPointsRef.current();
      }
    }
  }, [token]);

  const scheduleLiveKeyPoints = useCallback(() => {
    const transcript = latestRealtimeTranscriptRef.current;
    if (!liveNotesActiveRef.current
        || !transcript
        || keyPointTimerRef.current !== null
        || keyPointInFlightRef.current) return;
    const wordCount = transcript.content.split(/\s+/).filter(Boolean).length;
    if (wordCount < 8) return;

    // First useful bullets arrive quickly; later whole-transcript refreshes are deliberately
    // spaced so a three-minute answer cannot produce a model call on every short VAD turn.
    const minimumGap = liveKeyPoints.length === 0 ? 1_500 : 15_000;
    const elapsed = Date.now() - lastKeyPointRequestAtRef.current;
    keyPointTimerRef.current = window.setTimeout(() => {
      keyPointTimerRef.current = null;
      void extractLiveKeyPoints();
    }, Math.max(0, minimumGap - elapsed));
  }, [extractLiveKeyPoints, liveKeyPoints.length]);

  useEffect(() => {
    scheduleKeyPointsRef.current = scheduleLiveKeyPoints;
  }, [scheduleLiveKeyPoints]);

  const prepareRealtime = useCallback(
    async (stream: MediaStream): Promise<boolean> => {
      realtimeRef.current?.dispose();
      realtimeRef.current = null;
      realtimeEligibleForReuseRef.current = false;
      latestRealtimeTranscriptRef.current = undefined;
      setLiveKeyPoints([]);
      setLiveNotesState('connecting');

      const handle = await createRealtimeTranscription(apiClient, token, stream, {
        onStateChange: setLiveNotesState,
        onTranscript: (transcript) => {
          latestRealtimeTranscriptRef.current = transcript;
          scheduleKeyPointsRef.current();
        },
      });
      // A re-record or unmount may have replaced/stopped this source while setup was in flight.
      if (streamRef.current !== stream) {
        handle?.dispose();
        return false;
      }
      realtimeRef.current = handle;
      if (!handle) {
        realtimeEligibleForReuseRef.current = false;
        setLiveNotesState('unavailable');
      } else if (realtimeShouldListenRef.current) {
        // It joined after MediaRecorder had already started and may have missed opening words.
        // Do not spend on a partial realtime call that can never replace batch transcription.
        realtimeEligibleForReuseRef.current = false;
        realtimeRef.current = null;
        handle.dispose();
        setLiveNotesState('unavailable');
      } else {
        realtimeEligibleForReuseRef.current = true;
      }
      return true;
    },
    [token],
  );

  const finalize = useCallback(
    async (wasInterrupted: boolean) => {
      const session = sessionRef.current;
      if (!session) return;
      // Claim the session SYNCHRONOUSLY, before the first await. A second finalize during the
      // `await session.stop()` — the 3-min cap tick (every 250ms), a double-tap stop, or an
      // interruption firing mid-stop — then finds a null ref and no-ops, instead of calling
      // recorder.stop() again (InvalidStateError → the catch below discarding a perfectly good take).
      sessionRef.current = null;
      const duration = currentElapsedSeconds();
      realtimeShouldListenRef.current = false;
      liveNotesActiveRef.current = false;
      if (keyPointTimerRef.current !== null) {
        clearTimeout(keyPointTimerRef.current);
        keyPointTimerRef.current = null;
      }
      const realtime = realtimeRef.current;
      realtimeRef.current = null;
      const realtimeEligibleForReuse = realtimeEligibleForReuseRef.current;
      realtimeEligibleForReuseRef.current = false;
      const realtimeResult = realtime?.finish();
      if (realtimeResult) setPhase('finishing');
      try {
        const result = await session.stop();
        if (result.blob.size === 0) {
          await releaseStream();
          setErrorMessage("We didn't catch any audio — let's try again.");
          setPhase('intro');
          return;
        }
        // MediaRecorder is finished, so release the actual microphone immediately. The optional
        // provider may need a moment to close its final VAD turn, but that must not keep the phone's
        // mic indicator lit or retain the source track.
        const playbackReady = releaseStream(true);
        const finishedRealtimeTranscript = await realtimeResult;
        const realtimeTranscript = realtimeEligibleForReuse ? finishedRealtimeTranscript : undefined;
        // Straight into the store: from here until the server confirms the upload this blob is the
        // ONLY copy of the referee's three minutes, so it must not be reachable by an unmount.
        holdRecording(
          token,
          { blob: result.blob, durationSeconds: duration, realtimeTranscript },
          wasInterrupted,
        );
        // Keep the review player off-screen until WebKit has fully left its microphone audio route.
        await playbackReady;
        setPhase('review');
      } catch {
        await releaseStream();
        setErrorMessage("That recording didn't save. Please try again.");
        setPhase('intro');
      }
    },
    [currentElapsedSeconds, holdRecording, releaseStream, token],
  );

  const handleAcquireError = useCallback((err: unknown) => {
    const name = (err as { name?: string } | null)?.name;
    if (name === 'NotFoundError' || name === 'OverconstrainedError' || name === 'NotReadableError') {
      setErrorMessage(null);
      setPhase('nomic');
      return;
    }
    // NotAllowedError / SecurityError / anything else → treat as a permission block with re-enable help.
    setErrorMessage(null);
    setPhase('denied');
  }, []);

  // Freeze the take like a user pause (stops the recorder + the clock) without leaving the mic hot.
  // Used for a benign background auto-pause; the clock stays frozen so no silent stretch is banked.
  const autoPause = useCallback(() => {
    if (phaseRef.current !== 'recording') return;
    sessionRef.current?.pause();
    realtimeShouldListenRef.current = false;
    realtimeRef.current?.pause();
    pauseStartedAtRef.current = Date.now();
    setPhase('paused');
  }, []);

  const attachInterruption = useCallback(
    (stream: MediaStream) => {
      // Only one take's listeners at a time (reRecord re-acquires a fresh stream).
      detachInterruptionRef.current?.();

      const track = stream.getAudioTracks()[0];
      if (!track) {
        detachInterruptionRef.current = null;
        return;
      }

      const onEnded = () => {
        // A phone call or screen-lock ENDING the track mid-take: keep the partial, don't lose it.
        if (phaseRef.current === 'recording' || phaseRef.current === 'paused') void finalize(true);
      };
      const onMute = () => {
        // iOS backgrounding / an incoming call SUSPENDS the input — it fires `mute`, not `ended`,
        // and may suspend the recorder while the wall clock keeps ticking. Freeze immediately so we
        // never bank a silent stretch, and remember this was a real interruption (not a user pause).
        interruptedByMuteRef.current = true;
        autoPause();
      };
      const onUnmute = () => {
        // The input recovered (call ended / foregrounded) — clear the flag; the take stays paused so
        // the referee resumes when ready. (We never auto-resume: a race could clip their next words.)
        interruptedByMuteRef.current = false;
      };
      const onVisibility = () => {
        if (document.visibilityState === 'hidden') {
          // Backgrounded mid-take → auto-pause (safety first; a benign desktop tab switch, where the
          // track does NOT mute, simply pauses and can be resumed — no "cut short").
          autoPause();
        } else if (
          (phaseRef.current === 'recording' || phaseRef.current === 'paused') &&
          (track.muted || interruptedByMuteRef.current)
        ) {
          // Returned to the foreground but the mic is still muted/suspended → the take cannot safely
          // continue. Preserve what we have with the "cut short" copy rather than a silent gap.
          void finalize(true);
        }
      };

      track.addEventListener('ended', onEnded, { once: true });
      track.addEventListener('mute', onMute);
      track.addEventListener('unmute', onUnmute);
      document.addEventListener('visibilitychange', onVisibility);

      detachInterruptionRef.current = () => {
        track.removeEventListener('ended', onEnded);
        track.removeEventListener('mute', onMute);
        track.removeEventListener('unmute', onUnmute);
        document.removeEventListener('visibilitychange', onVisibility);
      };
    },
    [autoPause, finalize],
  );

  const acquire = useCallback(async (): Promise<MediaStream> => {
    return navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
  }, []);

  const beginCapture = useCallback((stream: MediaStream): boolean => {
    try {
      const mimeType = negotiateMimeType();
      const session = createRecorderSession(stream, mimeType);
      sessionRef.current = session;
      startedAtRef.current = Date.now();
      pausedAccumRef.current = 0;
      pauseStartedAtRef.current = null;
      lastAnnouncedRef.current = '';
      interruptedByMuteRef.current = false;
      setElapsed(0);
      setErrorMessage(null);
      // H6: the aria-live countdown region only mounts in the recording/paused phases. Setting the
      // "started" text BEFORE it mounts means SRs never announce it (text present at mount is not a
      // change). So mount it empty now, then set the message on a later task — once the region is
      // live, that write registers as a change and is spoken, matching the 1min/30s/10s milestones.
      setAnnounce('');
      if (announceTimerRef.current !== null) clearTimeout(announceTimerRef.current);
      session.start();
      setPhase('recording');
      announceTimerRef.current = window.setTimeout(() => {
        announceTimerRef.current = null;
        setAnnounce(`Recording started. About ${Math.round(maxSeconds / 60)} minutes.`);
      }, 0);
      return true;
    } catch {
      setErrorMessage("We couldn't start recording on this device.");
      setPhase('error');
      return false;
    }
  }, [maxSeconds]);

  const requestMicCheck = useCallback(async () => {
    if (!isRecordingSupported()) {
      setPhase('nomic');
      return;
    }
    setPhase('requesting');
    setErrorMessage(null);
    try {
      const stream = await acquire();
      streamRef.current = stream;
      attachInterruption(stream);
      meterRef.current = createLevelMeter(stream);
      heardRef.current = false;
      setHeard(false);
      setMicChecked(true);
      setPhase('ready');
      void prepareRealtime(stream);
    } catch (err) {
      handleAcquireError(err);
    }
  }, [acquire, attachInterruption, handleAcquireError, prepareRealtime, setMicChecked]);

  const startRecording = useCallback(() => {
    const stream = streamRef.current;
    if (!stream) {
      void requestMicCheck();
      return;
    }
    realtimeShouldListenRef.current = true;
    liveNotesActiveRef.current = true;
    realtimeEligibleForReuseRef.current = realtimeRef.current !== null;
    realtimeRef.current?.activate();
    if (!beginCapture(stream)) clearRealtime();
  }, [beginCapture, clearRealtime, requestMicCheck]);

  const reRecord = useCallback(async () => {
    // "Start over" is the referee choosing to discard the take, so it goes NOW — before the mic is
    // re-acquired, and whether or not that re-acquisition succeeds. Holding a discarded take back
    // as a silent safety net would keep someone's voice in memory after they asked us to drop it.
    clearRecording();
    clearRealtime();
    if (!isRecordingSupported()) {
      setPhase('nomic');
      return;
    }
    setPhase('requesting');
    try {
      // The take released the mic; re-acquire (no second prompt — permission persists) and go
      // straight back into a take so "Re-record" is a single tap.
      const stream = await acquire();
      streamRef.current = stream;
      attachInterruption(stream);
      meterRef.current = createLevelMeter(stream);
      // Unlike the initial mic-check path, re-recording has no dwell time in which to prewarm.
      // Wait here so the second take gets live notes from its first word too.
      if (!await prepareRealtime(stream)) return;
      realtimeShouldListenRef.current = true;
      liveNotesActiveRef.current = true;
      realtimeEligibleForReuseRef.current = realtimeRef.current !== null;
      realtimeRef.current?.activate();
      if (!beginCapture(stream)) clearRealtime();
    } catch (err) {
      handleAcquireError(err);
    }
  }, [acquire, attachInterruption, beginCapture, clearRealtime, clearRecording, handleAcquireError, prepareRealtime]);

  const pause = useCallback(() => {
    if (phaseRef.current !== 'recording') return;
    sessionRef.current?.pause();
    realtimeShouldListenRef.current = false;
    realtimeRef.current?.pause();
    pauseStartedAtRef.current = Date.now();
    setPhase('paused');
  }, []);

  const resume = useCallback(() => {
    if (phaseRef.current !== 'paused') return;
    if (pauseStartedAtRef.current) {
      pausedAccumRef.current += Date.now() - pauseStartedAtRef.current;
      pauseStartedAtRef.current = null;
    }
    sessionRef.current?.resume();
    realtimeShouldListenRef.current = true;
    realtimeRef.current?.resume();
    setPhase('recording');
  }, []);

  const stop = useCallback(() => {
    if (phaseRef.current !== 'recording' && phaseRef.current !== 'paused') return;
    void finalize(false);
  }, [finalize]);

  // Live level meter (drives the coral waveform + the mic-check "we can hear you").
  useEffect(() => {
    if (phase !== 'ready' && phase !== 'recording') return;
    const meter = meterRef.current;
    if (!meter) return;
    let raf = 0;
    const loop = () => {
      const next = meter.read();
      setLevel((prev) => (Math.abs(prev - next) > 0.02 ? next : prev));
      if (phase === 'ready' && next > HEARD_LEVEL && !heardRef.current) {
        heardRef.current = true;
        setHeard(true);
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [phase]);

  // The clock + the 3-minute hard cap.
  useEffect(() => {
    if (phase !== 'recording') return;
    const id = setInterval(() => {
      const secs = currentElapsedSeconds();
      setElapsed(secs);
      if (maxSeconds - secs <= 0) void finalize(false);
    }, 250);
    return () => clearInterval(id);
  }, [phase, maxSeconds, currentElapsedSeconds, finalize]);

  const remaining = Math.max(0, maxSeconds - elapsed);

  // Milestone announcements for the aria-live countdown (never every second — that would be noise).
  useEffect(() => {
    if (phase !== 'recording') return;
    let msg = '';
    if (remaining === 60) msg = 'One minute left.';
    else if (remaining === NEAR_LIMIT_SECONDS) msg = 'Thirty seconds left — wrap up when you are ready.';
    else if (remaining === 10) msg = 'Ten seconds left.';
    if (msg && lastAnnouncedRef.current !== msg) {
      lastAnnouncedRef.current = msg;
      setAnnounce(msg);
    }
  }, [remaining, phase]);

  return {
    phase,
    level,
    elapsed,
    remaining,
    nearLimit: (phase === 'recording' || phase === 'paused') && remaining <= NEAR_LIMIT_SECONDS,
    heard,
    announce,
    liveKeyPoints,
    liveNotesState,
    recording,
    interrupted: recording?.interrupted ?? false,
    errorMessage,
    requestMicCheck: () => void requestMicCheck(),
    startRecording,
    pause,
    resume,
    stop,
    reRecord: () => void reRecord(),
  };
}

/* ============================================================================================== *
 * Screen
 * ============================================================================================== */

export function Record() {
  const { token, context } = useSession();
  const { advance, pending, error } = useAdvance();
  const consentAcknowledged = useSessionStore((s) => s.consentAcknowledged);
  const setConsentAcknowledged = useSessionStore((s) => s.setConsentAcknowledged);
  const clearRecording = useSessionStore((s) => s.clearRecording);

  const maxSeconds = context?.maxRecordingSeconds ?? DEFAULT_MAX_SECONDS;
  const firstName = context?.candidate.name.split(' ')[0] ?? 'them';
  const isRerecord = context?.status === 'rerecord_requested';
  const prefersReducedMotion = usePrefersReducedMotion();

  const rec = useVoiceRecorder(maxSeconds, token);
  const prompts = useMemo(() => buildPrompts(firstName), [firstName]);
  const take = rec.recording;

  /** The attestation `/context` surfaced — echoed verbatim, exactly as the Confirm step does. */
  const consentAttestation = useMemo<ConsentAttestation | undefined>(
    () =>
      context?.consentDocumentKey && context.consentDocumentVersion
        ? { documentKey: context.consentDocumentKey, version: context.consentDocumentVersion }
        : undefined,
    [context],
  );

  /**
   * Send the take. `withConsent` re-posts the attestation first — the one-tap answer to the 422
   * below, which is otherwise the referee's dead end.
   *
   * The take is cleared ONLY once `uploadRecording` has resolved, i.e. once init → PUT → complete
   * have all succeeded and the server holds the audio. A throw anywhere in there leaves it held,
   * which is what makes "Your recording is safe" a true sentence rather than a hopeful one.
   */
  const [consentPath, setConsentPath] = useState(false);
  const send = useCallback(
    async (withConsent: boolean) => {
      if (!take) return;
      setConsentPath(withConsent);
      let uploaded = false;
      await advance(async () => {
        if (withConsent) {
          await apiClient.recordConsent(token, consentAttestation);
          setConsentAcknowledged(true);
        }
        const next = await uploadRecording(apiClient, token, take);
        uploaded = true;
        return next;
      }, 'processing');
      // After `advance` has navigated, so the review panel is never re-rendered take-less.
      if (uploaded) clearRecording();
    },
    [advance, clearRecording, consentAttestation, setConsentAcknowledged, take, token],
  );

  const onUse = useCallback(() => void send(false), [send]);
  const onConsentAndUse = useCallback(() => void send(true), [send]);

  const sendState = useMemo<SendState>(() => {
    const code = errorCodeOf(error);
    // A refused consent outranks everything: it is the one outcome with no way forward from here.
    if (code === CANNOT_CONSENT) return 'consent-refused';
    if (pending) return consentPath ? 'sending-consent' : 'sending';
    if (code === CONSENT_REQUIRED) return 'needs-consent';
    return error ? 'failed' : 'idle';
  }, [consentPath, error, pending]);

  // FIX H1 (defense-in-depth; the backend also enforces): consent is only ever posted from the
  // Confirm step. A returning referee deep-linking straight to `/record` would otherwise skip it, so
  // gate the route on this visit's ack flag. The normal flow sets the flag in Confirm before
  // advancing here, so a legitimate arrival is never bounced; a deep-link is sent to consent first.
  //
  // EXCEPT for a re-record. `rerecord_requested` is reached only by having already uploaded a take,
  // which `audio/init` will not presign without a consent row — so consent IS on record, and the
  // client has nothing left to gate. Worse, Confirm is a dead end at that status: BOTH
  // `confirm-details` and `consent` are `Accepted`-only server-side, so its submit 409s and there
  // is no way back (`/record` would just bounce them again). Every referee asked for a second take
  // would hit that on a fresh tab, where this visit's ack flag is necessarily unset.
  if (!consentAcknowledged && !isRerecord) {
    return <Navigate to={`/r/${token}/confirm`} replace />;
  }

  switch (rec.phase) {
    case 'requesting':
      return <BusyPanel label="Getting your microphone ready…" />;

    case 'finishing':
      return <BusyPanel label="Finishing your notes…" />;

    case 'ready':
      return (
        <MicCheckPanel
          heard={rec.heard}
          level={rec.level}
          animated={!prefersReducedMotion}
          onStart={rec.startRecording}
        />
      );

    case 'recording':
    case 'paused':
      return (
        <RecordingPanel
          firstName={firstName}
          prompts={prompts}
          paused={rec.phase === 'paused'}
          level={rec.level}
          remaining={rec.remaining}
          nearLimit={rec.nearLimit}
          announce={rec.announce}
          liveKeyPoints={rec.liveKeyPoints}
          liveNotesState={rec.liveNotesState}
          animated={!prefersReducedMotion}
          onPause={rec.pause}
          onResume={rec.resume}
          onStop={rec.stop}
        />
      );

    case 'review':
      // Take-less review means the upload just succeeded and `advance` is navigating this frame —
      // there is nothing to review and nothing has gone wrong, so say what is happening.
      if (!take) return <BusyPanel label="Sending your recording…" />;
      return (
        <ReviewPanel
          recording={take}
          interrupted={rec.interrupted}
          firstName={firstName}
          state={sendState}
          onUse={onUse}
          onConsentAndUse={onConsentAndUse}
          onReRecord={rec.reRecord}
        />
      );

    case 'denied':
      return <DeniedPanel onRetry={rec.requestMicCheck} />;

    case 'nomic':
      return <NoMicPanel />;

    case 'error':
      return <ErrorPanel message={rec.errorMessage} onRetry={rec.requestMicCheck} />;

    case 'intro':
    default:
      return (
        <IntroPanel
          firstName={firstName}
          isRerecord={isRerecord}
          errorMessage={rec.errorMessage}
          onStart={rec.requestMicCheck}
        />
      );
  }
}

/* ---------------------------------------------------------------------------------------------- *
 * Panels
 * ---------------------------------------------------------------------------------------------- */

function IntroPanel({
  firstName,
  isRerecord,
  errorMessage,
  onStart,
}: {
  firstName: string;
  isRerecord: boolean;
  errorMessage: string | null;
  onStart: () => void;
}) {
  return (
    <div className="flex flex-1 flex-col">
      {isRerecord && (
        <div className="mb-4 rounded-md bg-coral-tint/60 px-3.5 py-2.5 text-[12px] font-medium leading-relaxed text-coral-text">
          Thanks for coming back — one more take is all we need. Your earlier one wasn&rsquo;t used.
        </div>
      )}

      <h1 className="font-display text-[22px] font-semibold leading-[1.2] tracking-[-0.02em] text-ink text-balance">
        First, a quick mic check.
      </h1>
      <p className="mt-2 font-body text-[14px] leading-relaxed text-ink-70 text-pretty">
        Next we&rsquo;ll use your microphone. Nothing records until you tap the button, and you can
        always start over. You&rsquo;ll speak about working with {firstName} for around three minutes.
      </p>

      <ul className="mt-5 flex flex-col gap-2.5">
        <PrimeRow>One conversational question at a time — just talk, in your own words.</PrimeRow>
        <PrimeRow>Pause any time, and re-record the whole thing if you&rsquo;d like.</PrimeRow>
        <PrimeRow>{ASSURANCE}</PrimeRow>
      </ul>

      <div className="mt-auto flex flex-col gap-2.5 pt-6">
        <Button variant="coral" size="block" onClick={onStart}>
          Start mic check
        </Button>
        {errorMessage && (
          <p role="alert" className="text-center text-[12px] font-medium text-coral-text">
            {errorMessage}
          </p>
        )}
      </div>
    </div>
  );
}

function MicCheckPanel({
  heard,
  level,
  animated,
  onStart,
}: {
  heard: boolean;
  level: number;
  animated: boolean;
  onStart: () => void;
}) {
  return (
    <div className="flex flex-1 flex-col">
      <h1 className="font-display text-[22px] font-semibold leading-[1.2] tracking-[-0.02em] text-ink text-balance">
        Say hello.
      </h1>
      <p className="mt-2 font-body text-[14px] leading-relaxed text-ink-70 text-pretty">
        Watch the bars move so you know we can hear you. When you&rsquo;re ready, start recording.
      </p>

      <div className="mt-6 rounded-lg border border-line bg-surface px-4 py-6 text-center shadow-sm">
        <p className="font-display text-[12px] font-medium text-ink">Say hello and watch the bars move.</p>
        <div className="my-4">
          <Waveform level={level} animated={animated} live />
        </div>
        <p
          className={cn(
            'inline-flex items-center gap-1.5 rounded-pill px-3 py-1.5 font-display text-[11px] font-semibold transition-colors duration-ui',
            heard ? 'bg-sage-tint text-sage-text' : 'bg-paper-2 text-ink-quiet',
          )}
          role="status"
        >
          {heard ? (
            <>
              <CheckIcon /> We can hear you.
            </>
          ) : (
            'Listening for your voice…'
          )}
        </p>
      </div>

      <div className="mt-auto flex flex-col gap-2.5 pt-6">
        <Button variant="coral" size="block" onClick={onStart}>
          Start recording
        </Button>
        <p className="text-center text-[11px] font-medium leading-relaxed text-sage-text">{ASSURANCE}</p>
      </div>
    </div>
  );
}

function RecordingPanel({
  firstName,
  prompts,
  paused,
  level,
  remaining,
  nearLimit,
  announce,
  liveKeyPoints,
  liveNotesState,
  animated,
  onPause,
  onResume,
  onStop,
}: {
  firstName: string;
  prompts: string[];
  paused: boolean;
  level: number;
  remaining: number;
  nearLimit: boolean;
  announce: string;
  liveKeyPoints: string[];
  liveNotesState: LiveNotesState;
  animated: boolean;
  onPause: () => void;
  onResume: () => void;
  onStop: () => void;
}) {
  const [promptIndex, setPromptIndex] = useState(0);
  const hasNext = promptIndex < prompts.length - 1;

  return (
    <div className="flex flex-1 flex-col text-center">
      <div className="flex items-center justify-between text-left">
        <span className="font-display text-[11px] font-medium text-ink-quiet">Recording for {firstName}</span>
        <span
          className="inline-flex items-center gap-1.5 rounded-pill bg-coral-tint/70 px-2.5 py-1 font-display text-[10.5px] font-semibold text-coral-text"
          role="status"
        >
          <span
            className={cn('h-[7px] w-[7px] rounded-full bg-coral', animated && !paused && 'animate-pulse')}
          />
          {paused ? 'Paused' : 'Recording'}
        </span>
      </div>

      {/* Prompt card — advances manually, never on a timer. */}
      <div className="mt-4 rounded-lg border border-line bg-surface px-4 py-4 shadow-sm">
        <p className="font-display text-[15px] font-medium leading-snug text-ink">
          &ldquo;{prompts[promptIndex]}&rdquo;
        </p>
        <p className="mt-2 text-[11px] text-ink-quiet">Riff on it — there&rsquo;s no wrong answer.</p>
        <div className="mt-3 flex items-center justify-center gap-1.5" aria-hidden="true">
          {prompts.map((_, i) => (
            <span
              key={i}
              className={cn(
                'h-[5px] rounded-full transition-all duration-ui',
                i === promptIndex ? 'w-4 bg-coral' : 'w-[5px] bg-line-2',
              )}
            />
          ))}
        </div>
        {hasNext && (
          <button
            type="button"
            onClick={() => setPromptIndex((i) => Math.min(i + 1, prompts.length - 1))}
            className="mt-2 inline-flex min-h-[44px] items-center justify-center font-display text-[11.5px] font-semibold text-coral-text hover:underline"
          >
            Next question ›
          </button>
        )}
      </div>

      <div
        className="mt-3 rounded-lg border border-sage/25 bg-sage-tint/45 px-3.5 py-3 text-left"
        aria-live="polite"
      >
        <div className="flex items-center justify-between gap-3">
          <p className="font-display text-[11px] font-semibold text-sage-text">Key points so far</p>
          <span className="inline-flex items-center gap-1.5 rounded-pill bg-surface/80 px-2 py-1 font-display text-[9.5px] font-semibold text-sage-text">
            <span className={cn('h-1.5 w-1.5 rounded-full bg-sage-deep', !paused && 'animate-pulse')} />
            Live
          </span>
        </div>
        {liveKeyPoints.length > 0 ? (
          <ul className="mt-2 max-h-32 list-disc space-y-1 overflow-y-auto pl-4 pr-1 text-[11px] leading-snug text-ink-70">
            {liveKeyPoints.map((point, index) => (
              <li key={`${index}-${point}`}>{point}</li>
            ))}
          </ul>
        ) : (
          <p className="mt-2 text-[10.5px] leading-relaxed text-ink-quiet">
            {liveNotesState === 'unavailable'
              ? 'Live notes are unavailable, but your recording is safe.'
              : liveNotesState === 'connecting'
                ? 'Getting live notes ready…'
                : 'Listening for the useful details…'}
          </p>
        )}
        {liveKeyPoints.length > 0 && (
          <p className="mt-2 text-[9.5px] text-ink-quiet">
            {liveNotesState === 'updating' ? 'Tidying these up…' : 'These may tidy up as you keep talking.'}
          </p>
        )}
      </div>

      {/* Coral waveform (decorative) + the always-text countdown. */}
      <div className="mt-auto">
        <Waveform level={paused ? 0 : level} animated={animated && !paused} live={!paused} />
        <div className="mt-1">
          <div className="font-display text-[16px] font-semibold tracking-wide text-ink" aria-hidden="true">
            {formatClock(remaining)}
          </div>
          <div className={cn('text-[10.5px]', nearLimit ? 'font-medium text-coral-text' : 'text-ink-quiet')}>
            {nearLimit ? 'remaining — wrap up when you’re ready' : 'remaining'}
          </div>
        </div>

        {/* Screen-reader countdown: milestone announcements only. */}
        <p className="sr-only" role="timer" aria-live="polite">
          {announce}
        </p>

        <div className="mt-3 flex items-center justify-center gap-6">
          <button
            type="button"
            onClick={paused ? onResume : onPause}
            aria-label={paused ? 'Resume recording' : 'Pause recording'}
            className="grid h-11 w-11 place-items-center rounded-full border border-line-2 bg-surface text-coral-text transition-colors duration-ui hover:bg-paper-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-deep/25 focus-visible:ring-offset-2 focus-visible:ring-offset-paper"
          >
            {paused ? <PlayIcon /> : <PauseIcon />}
          </button>
          <button
            type="button"
            onClick={onStop}
            aria-label={`Stop recording, ${formatClock(remaining)} remaining`}
            className="grid h-16 w-16 place-items-center rounded-full bg-coral shadow-[0_0_0_6px_var(--coral-tint),0_10px_22px_-8px_rgba(217,112,74,0.7)] transition-transform duration-ui hover:scale-[1.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-deep/30 focus-visible:ring-offset-2 focus-visible:ring-offset-paper"
          >
            <span className="h-5 w-5 rounded-[6px] bg-white" />
          </button>
        </div>

        <button
          type="button"
          onClick={onStop}
          className="mt-3 inline-flex min-h-[44px] items-center justify-center text-[11.5px] font-medium text-ink-quiet hover:text-ink"
        >
          Stop &amp; review
        </button>
        <p className="mt-1 text-[10px] leading-relaxed text-ink-quiet">
          Please leave out other people&rsquo;s personal details.
        </p>
        <p className="mt-1.5 text-[11px] font-medium text-sage-text">{ASSURANCE}</p>
      </div>
    </div>
  );
}

/**
 * Stop → listen back → send. Every failure the send can produce is answered HERE, on this panel,
 * with the take still in hand: the referee has already given their three minutes, and a screen
 * that navigates away from them at the first refusal is a screen that throws them away.
 */
function ReviewPanel({
  recording,
  interrupted,
  firstName,
  state,
  onUse,
  onConsentAndUse,
  onReRecord,
}: {
  recording: CompletedRecording;
  interrupted: boolean;
  firstName: string;
  state: SendState;
  onUse: () => void;
  onConsentAndUse: () => void;
  onReRecord: () => void;
}) {
  const audioUrl = useObjectUrl(recording.blob);
  const sending = state === 'sending' || state === 'sending-consent';
  // The consent block stays up for the whole retry (`sending-consent`), not just until the tap:
  // dropping it the instant the request starts would shuffle the buttons under the referee's thumb.
  const askingConsent = state === 'needs-consent' || state === 'sending-consent';

  return (
    <div className="flex flex-1 flex-col">
      <h1 className="font-display text-[22px] font-semibold leading-[1.2] tracking-[-0.02em] text-ink text-balance">
        {interrupted ? 'Your recording was cut short.' : 'Have a listen back.'}
      </h1>
      <p className="mt-2 font-body text-[14px] leading-relaxed text-ink-70 text-pretty">
        {interrupted
          ? 'Something interrupted the recording, but we kept what you said. Use it, or start over.'
          : 'Happy with it? We’ll turn it into a letter — you approve every word before anything is sent.'}
      </p>

      <div className="mt-5 rounded-lg border border-line bg-surface p-4 shadow-sm">
        <div className="mb-2 flex items-center justify-between">
          <span className="font-display text-[11px] font-medium text-ink-quiet">Your recording</span>
          <span className="font-display text-[11px] font-semibold text-ink">
            {formatClock(recording.durationSeconds)}
          </span>
        </div>
        {audioUrl ? (
          <audio src={audioUrl} controls className="w-full" preload="metadata">
            Your browser can&rsquo;t play this recording, but you can still use it.
          </audio>
        ) : (
          <p className="text-[12px] text-ink-quiet">Recording ready.</p>
        )}
      </div>

      <div className="mt-auto flex flex-col gap-2.5 pt-6">
        {askingConsent && (
          // Same sage assurance block, same shield, same voice as the Confirm step's consent notice
          // — this is that notice arriving a screen later, not a new kind of warning.
          <div
            role="alert"
            className="flex items-start gap-2.5 rounded-md bg-sage-tint p-3.5 text-[11.5px] font-medium leading-relaxed text-sage-text"
          >
            <span className="flex-none pt-0.5">
              <ShieldCheckIcon />
            </span>
            <p>
              We still need your OK to record and transcribe your voice before we can take this.
              Your recording is safe &mdash; nothing has been sent.
            </p>
          </div>
        )}

        <Button
          variant="coral"
          size="block"
          disabled={sending}
          onClick={askingConsent ? onConsentAndUse : onUse}
        >
          {sending ? 'Sending your recording…' : askingConsent ? 'Agree and continue' : 'Use this recording'}
        </Button>

        {askingConsent && (
          // The canonical consent sentence, verbatim from Confirm — the wording the referee agrees
          // to must not drift between the two places we ask for it.
          <p className="text-center text-[11px] leading-relaxed text-ink-quiet">
            By continuing you agree we can record and transcribe your voice to draft the letter.
          </p>
        )}

        <Button variant="ghost" size="block" disabled={sending} onClick={onReRecord}>
          Re-record
        </Button>

        {state === 'failed' && (
          <p role="alert" className="text-center text-[12px] font-medium text-coral-text">
            That didn&rsquo;t go through — check your connection and try again. Your recording is safe.
          </p>
        )}
        {state === 'consent-refused' && (
          // An honest dead end, not a generic one: consent is missing AND the request will not take
          // it, which no tap on this screen can resolve. So it names the only two things that do
          // anything — wait it out, or tell the person who asked. No invented rescue, same posture
          // as the expired terminal page. What it will NOT do is imply the take was lost: it wasn't.
          <p role="alert" className="text-center text-[12px] font-medium leading-relaxed text-coral-text">
            We couldn&rsquo;t send this one just yet. Your recording is safe &mdash; try again in a
            moment, and if it keeps happening let {firstName} know.
          </p>
        )}
        <p className="text-center text-[11px] font-medium leading-relaxed text-sage-text">{ASSURANCE}</p>
      </div>
    </div>
  );
}

function DeniedPanel({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-1 flex-col">
      <h1 className="font-display text-[22px] font-semibold leading-[1.2] tracking-[-0.02em] text-ink text-balance">
        We&rsquo;ll need your microphone.
      </h1>
      <p className="mt-2 font-body text-[14px] leading-relaxed text-ink-70 text-pretty">
        It looks like microphone access is blocked. You can turn it back on and try again:
      </p>

      <ul className="mt-4 flex flex-col gap-2.5 text-[12.5px] leading-relaxed text-ink">
        <PrimeRow>
          Tap the <span className="font-medium">aA</span> or lock icon in your browser&rsquo;s address bar,
          then allow the microphone.
        </PrimeRow>
        <PrimeRow>On iPhone: Settings → the browser you&rsquo;re using → Microphone.</PrimeRow>
        <PrimeRow>Then come back here and tap try again.</PrimeRow>
      </ul>

      <div className="mt-auto flex flex-col gap-2.5 pt-6">
        <Button variant="coral" size="block" onClick={onRetry}>
          Try again
        </Button>
        <p className="text-center text-[11px] leading-relaxed text-ink-quiet">
          Still stuck? You can reopen this same link on another phone with a microphone.
        </p>
      </div>
    </div>
  );
}

function NoMicPanel() {
  return (
    <div className="flex flex-1 flex-col">
      <h1 className="font-display text-[22px] font-semibold leading-[1.2] tracking-[-0.02em] text-ink text-balance">
        No microphone here.
      </h1>
      <p className="mt-2 font-body text-[14px] leading-relaxed text-ink-70 text-pretty">
        We couldn&rsquo;t find a microphone on this device, or this browser can&rsquo;t record. The
        easiest fix is to open this same link on your phone.
      </p>
      <ul className="mt-4 flex flex-col gap-2.5 text-[12.5px] leading-relaxed text-ink">
        <PrimeRow>Open the email or message on your phone and tap the link again.</PrimeRow>
        <PrimeRow>Use Safari on iPhone, or Chrome on Android, for the smoothest recording.</PrimeRow>
      </ul>
      <div className="mt-auto pt-6">
        <p className="text-center text-[11px] leading-relaxed text-ink-quiet">
          Your link stays valid — nothing is lost by switching devices.
        </p>
      </div>
    </div>
  );
}

function ErrorPanel({ message, onRetry }: { message: string | null; onRetry: () => void }) {
  return (
    <div className="flex flex-1 flex-col">
      <h1 className="font-display text-[22px] font-semibold leading-[1.2] tracking-[-0.02em] text-ink text-balance">
        Let&rsquo;s try that again.
      </h1>
      <p className="mt-2 font-body text-[14px] leading-relaxed text-ink-70 text-pretty">
        {message ?? 'Something went wrong with the recording. Nothing was sent.'}
      </p>
      <div className="mt-auto pt-6">
        <Button variant="coral" size="block" onClick={onRetry}>
          Try again
        </Button>
      </div>
    </div>
  );
}

function BusyPanel({ label }: { label: string }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center text-center" aria-live="polite" role="status">
      <span className="brand-pulse h-3 w-3 rounded-full bg-coral" />
      <p className="mt-3 font-display text-[13px] font-medium text-ink-70">{label}</p>
    </div>
  );
}

/* ---------------------------------------------------------------------------------------------- *
 * Bits
 * ---------------------------------------------------------------------------------------------- */

const BAR_WEIGHTS = [0.32, 0.58, 0.85, 0.5, 1, 0.72, 0.92, 0.55, 1, 0.66, 0.82, 0.44, 0.3];
const BAR_MIN = 8;
const BAR_MAX = 48;

/** Decorative coral waveform. aria-hidden — time remaining is always text, never bars alone. */
function Waveform({ level, animated, live }: { level: number; animated: boolean; live: boolean }) {
  return (
    <div className="flex h-14 items-center justify-center gap-[3px]" aria-hidden="true">
      {BAR_WEIGHTS.map((weight, i) => {
        // Reduced motion (or a browser with no Web Audio) rests the bars at a calm mid height.
        const height = animated ? BAR_MIN + level * (BAR_MAX - BAR_MIN) * weight : 22;
        return (
          <span
            key={i}
            style={{ height: `${Math.max(BAR_MIN, Math.round(height))}px` }}
            className={cn(
              'w-1 rounded-full transition-[height] duration-100 ease-out',
              live ? 'bg-coral' : 'bg-coral/40',
            )}
          />
        );
      })}
    </div>
  );
}

function PrimeRow({ children }: { children: ReactNode }) {
  return (
    <li className="flex items-start gap-2.5 text-[12.5px] leading-snug text-ink">
      <span className="flex-none pt-0.5 text-sage-deep">
        <CheckIcon />
      </span>
      <span>{children}</span>
    </li>
  );
}

/** Object URL for the recorded blob, revoked on change/unmount (no leaked blob: handles). */
function useObjectUrl(blob: Blob | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob) {
      // The URL is browser-owned external state; clearing it here keeps a revoked prior URL out of
      // the player when a take is discarded.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob]);
  return url;
}

/* --- icons ------------------------------------------------------------------------------------ */

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-3.5 w-3.5" aria-hidden="true">
      <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** The same shield the Confirm step's consent notice uses — one consent mark across the flow. */
function ShieldCheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4" aria-hidden="true">
      <path
        d="M12 2l7 4v6c0 5-3.5 8-7 10-3.5-2-7-5-7-10V6z"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinejoin="round"
      />
      <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-[15px] w-[15px]" aria-hidden="true">
      <path d="M9 6.5v11M15 6.5v11" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-[15px] w-[15px]" aria-hidden="true">
      <path d="M8 5.5v13l11-6.5z" />
    </svg>
  );
}
